/**
 * Custom hook for auto-responder search and filtering functionality
 *
 * Provides state management and API integration for searching and filtering
 * auto-responders with debounced search, tag management, and efficient queries.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@apollo/client";
import { LIST_AUTO_RESPONDERS } from "../services/graphql/auto-responder-operations";
import type { AutoResponder } from "../types/auto-responder";

export interface AutoResponderFilters {
  search?: string;
  isActive?: boolean;
  tags?: string[];
  conversationId?: string;
}

export interface UseAutoResponderSearchOptions {
  limit?: number;
  initialFilters?: AutoResponderFilters;
  enableRealTimeSearch?: boolean;
}

export interface UseAutoResponderSearchResult {
  // Data
  autoResponders: AutoResponder[];
  loading: boolean;
  error: any;

  // Filters
  filters: AutoResponderFilters;
  setFilters: (filters: AutoResponderFilters) => void;
  clearFilters: () => void;

  // Metadata
  availableTags: string[];
  totalCount: number;
  hasNextPage: boolean;

  // Actions
  refetch: () => void;
  loadMore: () => void;
}

export function useAutoResponderSearch(
  options: UseAutoResponderSearchOptions = {}
): UseAutoResponderSearchResult {
  const {
    limit = 50,
    initialFilters = {},
    enableRealTimeSearch = true,
  } = options;

  const [filters, setFilters] = useState<AutoResponderFilters>(initialFilters);
  const [allAutoResponders, setAllAutoResponders] = useState<AutoResponder[]>(
    []
  );

  // Build GraphQL filter from our filters
  const graphqlFilter = useMemo(() => {
    const filter: any = {};

    if (filters.search) {
      filter.search = filters.search;
    }

    if (filters.isActive !== undefined) {
      filter.isActive = filters.isActive;
    }

    if (filters.tags && filters.tags.length > 0) {
      filter.tags = filters.tags;
    }

    if (filters.conversationId) {
      filter.conversationId = filters.conversationId;
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [filters]);

  // Main query for auto-responders
  const { data, loading, error, refetch, fetchMore } = useQuery(
    LIST_AUTO_RESPONDERS,
    {
      variables: {
        filter: graphqlFilter,
        limit,
      },
      notifyOnNetworkStatusChange: true,
      errorPolicy: "all",
    }
  );

  // Extract auto-responders from query result
  const autoResponders = useMemo(() => {
    return data?.listAutoResponders?.items || [];
  }, [data]);

  // Update all auto-responders when data changes
  useEffect(() => {
    if (autoResponders.length > 0) {
      setAllAutoResponders(autoResponders);
    }
  }, [autoResponders]);

  // Extract available tags from all auto-responders
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();

    allAutoResponders.forEach((responder) => {
      if (responder.tags) {
        responder.tags.forEach((tag) => tagSet.add(tag));
      }
    });

    return Array.from(tagSet).sort();
  }, [allAutoResponders]);

  // Get total count and pagination info
  const totalCount = data?.listAutoResponders?.totalCount || 0;
  const hasNextPage = !!data?.listAutoResponders?.nextToken;

  // Load more function for pagination
  const loadMore = async () => {
    if (!hasNextPage || loading) return;

    try {
      await fetchMore({
        variables: {
          filter: graphqlFilter,
          limit,
          nextToken: data?.listAutoResponders?.nextToken,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.listAutoResponders?.items) return prev;

          return {
            ...prev,
            listAutoResponders: {
              ...prev.listAutoResponders,
              items: [
                ...prev.listAutoResponders.items,
                ...fetchMoreResult.listAutoResponders.items,
              ],
              nextToken: fetchMoreResult.listAutoResponders.nextToken,
              totalCount: fetchMoreResult.listAutoResponders.totalCount,
            },
          };
        },
      });
    } catch (error) {
      console.error("Error loading more auto-responders:", error);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
  };

  return {
    // Data
    autoResponders,
    loading,
    error,

    // Filters
    filters,
    setFilters,
    clearFilters,

    // Metadata
    availableTags,
    totalCount,
    hasNextPage,

    // Actions
    refetch,
    loadMore,
  };
}

/**
 * Hook for searching auto-responders with advanced text search
 */
export function useAutoResponderTextSearch(
  searchQuery: string,
  options: {
    includeInactive?: boolean;
    tags?: string[];
    conversationId?: string;
    limit?: number;
  } = {}
) {
  const { includeInactive = false, tags, conversationId, limit = 25 } = options;

  const filters: AutoResponderFilters = {
    search: searchQuery.trim() || undefined,
    isActive: includeInactive ? undefined : true,
    tags,
    conversationId,
  };

  return useAutoResponderSearch({
    initialFilters: filters,
    limit,
    enableRealTimeSearch: true,
  });
}

/**
 * Hook for getting auto-responders grouped by tags
 */
export function useAutoRespondersByTags(
  options: {
    includeInactive?: boolean;
    limit?: number;
  } = {}
) {
  const { includeInactive = false, limit = 100 } = options;

  const { autoResponders, loading, error } = useAutoResponderSearch({
    initialFilters: {
      isActive: includeInactive ? undefined : true,
    },
    limit,
  });

  const groupedByTags = useMemo(() => {
    const groups: Record<string, AutoResponder[]> = {};

    autoResponders.forEach((responder) => {
      if (!responder.tags || responder.tags.length === 0) {
        // Group untagged items under "untagged"
        if (!groups["untagged"]) {
          groups["untagged"] = [];
        }
        groups["untagged"].push(responder);
      } else {
        // Add to each tag group
        responder.tags.forEach((tag) => {
          if (!groups[tag]) {
            groups[tag] = [];
          }
          groups[tag].push(responder);
        });
      }
    });

    return groups;
  }, [autoResponders]);

  return {
    groupedByTags,
    loading,
    error,
    totalCount: autoResponders.length,
  };
}
